import QRCode from 'qrcode';
import { type ReactNode, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { AdminButton } from '@/components/admin/button';
import { CategoryPicker } from '@/components/admin/category-picker';
import { ConvertToSimpleModal } from '@/components/admin/convert-to-simple-modal';
import { AdminIcon } from '@/components/admin/icon';
import { Popover } from '@/components/admin/popover';
import { PageHead } from '@/components/admin/page-head';
import { Tag } from '@/components/admin/tag';
import { useConfirm, useToast } from '@/components/ui/feedback';
import { Modal } from '@/components/ui/modal';
import { env } from '@/config/env';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useCategoriesList,
  useDeleteVariant,
  useDeleteProductMedia,
  useEnhanceProductImages,
  useHubs,
  usePackPreview,
  useProduct,
  useProductTransition,
  useUpdateProduct,
  useUpdateVariant,
} from '@/hooks/use-catalog';
import { usePageTitle } from '@/hooks/use-page-title';
import { apparenceVariante } from '@/lib/catalog/apparence';
import { accord, pluriel } from '@/lib/pluriel';
import { type Product, type ProductVariant, readProductOptions } from '@/lib/catalog/types';
import { useQuery } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api';
import { fmtFCFA } from '@/lib/format';
import type { TagClass } from '@/lib/status';
import { useMarketingCart } from '@/stores/marketing-cart.store';

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const STATUS_BADGE: Record<Product['status'], { l: string; cls: TagClass }> = {
  draft: { l: 'Brouillon', cls: 'neutral' },
  published: { l: 'Publié', cls: 'success' },
  paused: { l: 'En pause', cls: 'warning' },
  archived: { l: 'Archivé', cls: 'neutral' },
};

function variantStockTotal(v: ProductVariant): number {
  return v.stock.reduce((a, s) => a + s.quantity, 0);
}

function variantReservedTotal(v: ProductVariant): number {
  return v.stock.reduce((a, s) => a + s.reservedQuantity, 0);
}

function totalStock(p: Product): number {
  return p.variants.reduce((a, v) => a + variantStockTotal(v), 0);
}

function totalReserved(p: Product): number {
  return p.variants.reduce((a, v) => a + variantReservedTotal(v), 0);
}

function primaryHub(p: Product, hubsByCode: Map<string, { code: string; city: string }>): string {
  for (const v of p.variants) {
    for (const s of v.stock) {
      const h = hubsByCode.get(s.hubId);
      if (h) return `${h.code} · ${h.city}`;
    }
  }
  return '—';
}

function activeOfferPrice(p: Product, v: ProductVariant): number {
  const active = v.offers.find((o) => o.status === 'active');
  if (active) return active.price;
  return v.priceOverride ?? p.basePrice;
}

function lowestCostPrice(p: Product): number | null {
  let best: number | null = null;
  for (const v of p.variants) {
    if (v.costPrice != null) {
      best = best == null ? v.costPrice : Math.min(best, v.costPrice);
    }
  }
  return best;
}

function formatDate(d: string | null | Date | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** P18 — composition d'un pack en lecture seule (l'édition passe par le wizard). */
/**
 * P19 — grille de paliers njangi en lecture seule : le pendant « détail » de
 * l'éditeur du wizard (Tarification & stock). Rendue seulement si le produit
 * est éligible — l'absence de carte signifie « fermé à l'achat groupé ».
 */
function NjangiTiersCard({ productId }: { productId: string }) {
  const { data } = useQuery({
    queryKey: ['njangi-tiers', productId],
    queryFn: () =>
      unwrap<{
        eligible: boolean;
        basePrice: number;
        costPrice: number | null;
        tiers: Array<{
          minThreshold: number;
          discountPct: number | null;
          unitPrice: number | null;
          resolvedPrice: number;
          belowCost: boolean;
        }>;
      }>(api.get(`/admin/catalog/products/${productId}/njangi-tiers`)),
  });
  if (!data?.eligible) return null;
  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>Njangi · achat groupé — {data.tiers.length} palier(s)</h3>
          <p className="sub muted">
            Produit ouvert à l'achat groupé. La grille se modifie dans le wizard
            (Tarification &amp; stock) ; le créateur d'un groupe choisit une cible, jamais un
            prix.
          </p>
        </div>
      </div>
      {/* margin: 0 — même neutralisation que la carte Variantes : .table-wrap
          porte des marges négatives pensées pour les tableaux pleine page. */}
      <div className="table-wrap" style={{ margin: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Dès (seuil)</th>
              <th>Type</th>
              <th className="right">Prix obtenu</th>
            </tr>
          </thead>
          <tbody>
            {data.tiers.map((t) => (
              <tr key={t.minThreshold}>
                <td>{t.minThreshold} unités / participants</td>
                <td>
                  {t.unitPrice != null ? 'Prix ferme' : `Remise ${t.discountPct ?? 0} %`}
                </td>
                <td
                  className="right"
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                    color: t.belowCost ? 'var(--a-danger)' : undefined,
                  }}
                >
                  {fmtFCFA(t.resolvedPrice)}
                  {t.belowCost ? ' ⚠️ sous coût' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PackCompositionCard({ productId }: { productId: string }) {
  const { data: preview, isLoading } = usePackPreview(productId);
  const items = preview?.items ?? [];
  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>Composition du pack · {items.length} article(s)</h3>
          <p className="sub muted">
            {preview
              ? `Valeur des composants ${fmtFCFA(preview.componentsTotal)}${
                  preview.savings != null ? ` · économie client ${fmtFCFA(preview.savings)}` : ''
                } · ${preview.availability} pack(s) disponible(s)${
                  preview.weakestLink
                    ? ` · maillon faible : ${preview.weakestLink.productTitle}`
                    : ''
                }`
              : 'Chargement…'}
          </p>
        </div>
      </div>
      {isLoading ? (
        <div className="card-b muted">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="empty" style={{ margin: 16 }}>
          Aucun composant — complétez la composition dans le wizard avant publication.
        </div>
      ) : (
        <div className="table-wrap" style={{ margin: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Article</th>
                <th>SKU</th>
                <th className="right">Qté / pack</th>
                <th className="right">PU</th>
                <th className="right">Sous-total</th>
                <th className="right">Packs possibles</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>
                    <strong>{it.productTitle}</strong>
                    {it.variantName && it.variantName !== 'Standard' && (
                      <span className="muted"> — {it.variantName}</span>
                    )}
                    {!it.variantActive && (
                      <>
                        {' '}
                        <Tag variant="danger" noDot>
                          Inactive
                        </Tag>
                      </>
                    )}
                  </td>
                  <td className="mono" style={{ fontSize: 11.5 }}>
                    {it.variantSku}
                  </td>
                  <td className="right tn">{it.quantity}</td>
                  <td className="right tn">{it.unitPrice != null ? fmtFCFA(it.unitPrice) : '—'}</td>
                  <td className="right tn">{it.subtotal != null ? fmtFCFA(it.subtotal) : '—'}</td>
                  <td className="right tn">{it.packsPossible}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Apparence d'une variante : sa pastille de couleur et son visuel.
 *
 * Ni l'une ni l'autre ne vivent sur la variante — elles sont portées par la
 * valeur d'option correspondante (`attributes.options`), et le média par son
 * identifiant, qu'il faut résoudre dans les visuels du produit. Le tableau
 * n'affichait donc que le libellé : « Noir » sans moyen de voir que la teinte
 * enregistrée est un brun, ni quelle photo part sur la boutique.
 */
/**
 * Actions d'une déclinaison.
 *
 * Le bouton « … » existait mais n'était relié à rien : il s'enfonçait sous le
 * clic et ne faisait rien. On y place les gestes qui se décident depuis la
 * ligne elle-même, et uniquement ceux que les permissions autorisent — un
 * opérateur catalogue n'a pas le droit de supprimer.
 */
function MenuVariante({
  produitId,
  variante,
  hex,
}: {
  produitId: string;
  variante: ProductVariant;
  hex?: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { has, isSuperAdmin } = usePermissions();
  const majVariante = useUpdateVariant(produitId);
  const supprVariante = useDeleteVariant(produitId);

  const peutEcrire = isSuperAdmin || has('CATALOG.WRITE');
  const peutSupprimer = isSuperAdmin || has('CATALOG.DELETE');

  const copier = async (valeur: string, quoi: string) => {
    try {
      await navigator.clipboard.writeText(valeur);
      toast.success(`${quoi} copié.`);
    } catch {
      toast.error('Copie impossible — votre navigateur l’a refusée.');
    }
  };

  const basculerActivation = async (fermer: () => void) => {
    fermer();
    try {
      await majVariante.mutateAsync({
        id: variante.id,
        payload: { isActive: !variante.isActive },
      });
      toast.success(
        variante.isActive
          ? `${variante.name} ne sera plus proposée.`
          : `${variante.name} est de nouveau proposée.`,
      );
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Changement impossible'));
    }
  };

  const supprimer = async (fermer: () => void) => {
    fermer();
    const ok = await confirm({
      title: `Supprimer la déclinaison ${variante.name}`,
      message: (
        <>
          Son stock restant sera ramené à zéro, ses offres mises en pause, et
          son historique de prix part avec elle. Pour la retirer de la vente
          sans rien perdre, désactivez-la plutôt.
        </>
      ),
      confirmLabel: 'Supprimer',
      tone: 'danger',
      requireText: variante.sku,
      requireHint: <>Tapez <strong>{variante.sku}</strong> pour confirmer.</>,
    });
    if (!ok) return;
    try {
      // Le forçage vide le stock au passage : sans lui, le serveur refusait la
      // suppression d'une déclinaison encore approvisionnée, sans dire comment
      // s'en sortir. Les unités réservées par une commande restent, elles,
      // bloquantes côté serveur.
      await supprVariante.mutateAsync({ id: variante.id, force: true });
      toast.success(`${variante.name} supprimée.`);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Suppression impossible'));
    }
  };

  return (
    <Popover
      align="right"
      width={232}
      testId={`variant-menu-${variante.sku}`}
      trigger={
        <button type="button" className="top-btn" aria-label={`Actions sur ${variante.name}`}>
          <AdminIcon n="dots" s={14} />
        </button>
      }
    >
      {(fermer) => (
        <div>
          <button
            type="button"
            className="popover-item"
            onClick={() => {
              fermer();
              navigate(`/catalogue/${produitId}/modifier`);
            }}
          >
            <AdminIcon n="edit" s={13} />
            Modifier la déclinaison
          </button>

          {peutEcrire && (
            <button
              type="button"
              className="popover-item"
              onClick={() => void basculerActivation(fermer)}
              disabled={majVariante.isPending}
            >
              <AdminIcon n={variante.isActive ? 'eyeOff' : 'eye'} s={13} />
              {variante.isActive ? 'Retirer de la vente' : 'Remettre en vente'}
            </button>
          )}

          <button
            type="button"
            className="popover-item"
            onClick={() => {
              fermer();
              void copier(variante.sku, 'SKU');
            }}
          >
            <AdminIcon n="copy" s={13} />
            Copier le SKU
          </button>

          {hex && (
            <button
              type="button"
              className="popover-item"
              onClick={() => {
                fermer();
                void copier(hex.toUpperCase(), 'Code couleur');
              }}
            >
              <span className="variant-pastille" style={{ background: hex }} aria-hidden="true" />
              Copier {hex.toUpperCase()}
            </button>
          )}

          {peutSupprimer && (
            <>
              <div className="popover-sep" />
              <button
                type="button"
                className="popover-item danger"
                onClick={() => void supprimer(fermer)}
                disabled={supprVariante.isPending}
              >
                <AdminIcon n="close" s={13} />
                Supprimer
              </button>
            </>
          )}
        </div>
      )}
    </Popover>
  );
}

export function AdminProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading } = useProduct(id);
  const publishMut = useProductTransition('publish');
  const unpublishMut = useProductTransition('unpublish');
  const archiveMut = useProductTransition('archive');
  const enhanceImagesMut = useEnhanceProductImages(id ?? '');
  const deleteMediaMut = useDeleteProductMedia(id ?? '');
  const updateMut = useUpdateProduct(id ?? '');
  const confirm = useConfirm();
  const toast = useToast();
  const addToMarketingCart = useMarketingCart((s) => s.add);
  const inMarketingCart = useMarketingCart((s) => (id ? s.items.some((i) => i.id === id) : false));
  const { data: cats } = useCategoriesList({ isActive: true });
  const { data: hubs = [] } = useHubs();
  // Lightbox (index de l'image affichée en grand), drag & drop, changement rapide de catégorie.
  const [preview, setPreview] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [catModal, setCatModal] = useState(false);
  // Sélection de déclinaisons : l'import en fabrique huit là où le produit n'en
  // a qu'une, et les retirer une par une derrière un menu coûtait huit
  // confirmations pour un geste unique.
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [suppressionLot, setSuppressionLot] = useState(false);
  const [conversionSimple, setConversionSimple] = useState(false);
  const [catValue, setCatValue] = useState('');
  const supprimerDecli = useDeleteVariant(id ?? '');
  const { has: aDroit, isSuperAdmin: estSuperAdmin } = usePermissions();
  const peutSupprimerDecli = estSuperAdmin || aDroit('CATALOG.DELETE');
  usePageTitle(product ? `${product.title} · Catalogue` : 'Catalogue');

  if (isLoading || !product) {
    return (
      <div className="card">
        <div className="card-b muted" style={{ textAlign: 'center', padding: 24 }}>
          Chargement…
        </div>
      </div>
    );
  }

  const status = STATUS_BADGE[product.status];
  const category = cats?.find((c) => c.id === product.categoryId);
  const parentCategoryName = category?.name ?? '—';
  const hubsByCode = new Map(hubs.map((h) => [h.id, h] as const));
  const stockSum = totalStock(product);
  const reservedSum = totalReserved(product);
  const availableSum = Math.max(0, stockSum - reservedSum);
  const stockMin = Math.max(0, ...product.variants.map((v) => v.stock[0]?.safetyStock ?? 0));
  const hubLabel = primaryHub(product, hubsByCode);
  const costMin = lowestCostPrice(product);
  const marge = costMin != null ? product.basePrice - costMin : null;
  const margePct =
    marge != null && costMin && costMin > 0 ? (marge / product.basePrice) * 100 : null;

  const topVariants = [...product.variants]
    .map((v) => ({
      k: v.name,
      n: variantStockTotal(v),
      sold: 0,
    }))
    .sort((a, b) => b.n - a.n)
    .slice(0, 4);
  const topMax = topVariants[0]?.n || 1;

  const declinaisons = product.variants ?? [];
  // Après un passage en produit simple, les déclinaisons citées par d'anciennes
  // commandes restent en base, désactivées : le produit n'en est pas moins simple.
  const actives = declinaisons.filter((v) => v.isActive);
  const archivees = declinaisons.length - actives.length;
  const produitSimple =
    declinaisons.length <= 1 ||
    (actives.length === 1 && readProductOptions(product.attributes).length === 0);
  const reference = actives[0] ?? declinaisons[0];
  const selectionnees = declinaisons.filter((v) => selection.has(v.id));

  /**
   * Supprime plusieurs déclinaisons d'un geste.
   *
   * Les appels partent en série et non en bloc : le serveur refuse une
   * déclinaison dont des unités sont réservées par une commande, et il faut
   * pouvoir dire laquelle. Un échec n'annule donc pas les suppressions déjà
   * faites — le compte rendu l'énonce plutôt que de le taire.
   */
  const supprimerLot = async (cibles: typeof declinaisons, titre: string, message: ReactNode) => {
    const ok = await confirm({
      title: titre,
      message,
      confirmLabel: `Supprimer ${pluriel(cibles.length, 'déclinaison')}`,
      tone: 'danger',
      requireText: product.sku,
      requireHint: (
        <>
          Tapez <strong>{product.sku}</strong> pour confirmer.
        </>
      ),
    });
    if (!ok) return;

    setSuppressionLot(true);
    const refus: string[] = [];
    let faites = 0;
    for (const v of cibles) {
      try {
        await supprimerDecli.mutateAsync({ id: v.id, force: true });
        faites += 1;
      } catch (e) {
        refus.push(`${v.name} — ${extractErrorMessage(e, 'refusée')}`);
      }
    }
    setSuppressionLot(false);
    setSelection(new Set());

    if (faites) toast.success(`${pluriel(faites, 'déclinaison')} ${accord(faites, 'supprimée')}.`);
    if (refus.length) toast.error(refus.join(' · '));
  };

  const publish = async () => {
    try {
      await publishMut.mutateAsync(product.id);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Publication refusée'));
    }
  };

  const archiveProduct = async () => {
    const ok = await confirm({
      title: 'Archiver le produit',
      message: <>Le produit sera retiré de la vente. Vous pourrez le réactiver plus tard.</>,
      confirmLabel: 'Archiver',
      tone: 'default',
    });
    if (ok) archiveMut.mutate(product.id);
  };

  const enhanceImages = async () => {
    const ok = await confirm({
      title: "Améliorer les images avec l'IA",
      message: (
        <>
          Toutes les images de ce produit vont être régénérées (studio, filigrane Akwaba, badge
          contenance) et remplaceront définitivement les images actuelles. Cette action est{' '}
          <strong>irréversible</strong> et ne pourra pas être relancée une fois terminée.
        </>
      ),
      confirmLabel: 'Améliorer',
      tone: 'default',
    });
    if (!ok) return;
    try {
      await enhanceImagesMut.mutateAsync();
      toast.success('Images améliorées avec succès');
    } catch (e) {
      toast.error(extractErrorMessage(e, "Échec de l'amélioration des images"));
    }
  };

  const removeImage = async (mediaId: string, label: string) => {
    const ok = await confirm({
      title: "Supprimer l'image",
      message: <>« {label} » sera retirée du produit. Le fichier reste conservé.</>,
      confirmLabel: 'Supprimer',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteMediaMut.mutateAsync(mediaId);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Échec de la suppression de l'image"));
    }
  };

  const publicUrl = `${env.storefrontUrl}/fr/produit/${product.slug}`;

  const openOnSite = () => {
    window.open(publicUrl, '_blank', 'noopener');
  };

  /** Étiquette imprimable (62×40 mm) : marque, titre, SKU, prix + QR vers la fiche publique. */
  const printLabel = async () => {
    let qr = '';
    try {
      qr = await QRCode.toDataURL(publicUrl, { width: 220, margin: 1 });
    } catch {
      /* QR indisponible : étiquette sans QR */
    }
    const w = window.open('', '_blank', 'width=460,height=360');
    if (!w) {
      toast.error("Popup bloquée — autorise les popups pour imprimer l'étiquette.");
      return;
    }
    w.document
      .write(`<!doctype html><html><head><meta charset="utf-8"><title>Étiquette ${escapeHtml(product.sku)}</title><style>
      @page { size: 62mm 40mm; margin: 0; }
      * { box-sizing: border-box; }
      body { margin: 0; font-family: -apple-system, 'Segoe UI', Arial, sans-serif; }
      .label { width: 62mm; height: 40mm; padding: 3mm; display: flex; gap: 3mm; align-items: center; border: 1px dashed #ccc; }
      @media print { .label { border: none; } }
      .info { flex: 1; min-width: 0; }
      .brand { font-size: 7pt; letter-spacing: .08em; text-transform: uppercase; color: #555; }
      .title { font-size: 9pt; font-weight: 700; line-height: 1.25; margin: 1mm 0; max-height: 11mm; overflow: hidden; }
      .sku { font-family: ui-monospace, 'Courier New', monospace; font-size: 10pt; font-weight: 700; letter-spacing: .05em; }
      .price { font-size: 13pt; font-weight: 800; margin-top: 1mm; }
      img.qr { width: 22mm; height: 22mm; }
    </style></head><body>
      <div class="label">
        <div class="info">
          <div class="brand">Akwaba${product.brand ? ' · ' + escapeHtml(product.brand) : ''}</div>
          <div class="title">${escapeHtml(product.title)}</div>
          <div class="sku">${escapeHtml(product.sku)}</div>
          <div class="price">${escapeHtml(fmtFCFA(product.basePrice))}</div>
        </div>
        ${qr ? `<img class="qr" src="${qr}" alt="QR" />` : ''}
      </div>
      <script>window.onload = function () { window.print(); };</script>
    </body></html>`);
    w.document.close();
  };

  /** Réordonne les images (drag & drop) : la 1ʳᵉ devient la photo principale (hero). */
  const moveMedia = async (from: number, to: number) => {
    if (from === to) return;
    const links = [...product.mediaLinks];
    const [moved] = links.splice(from, 1);
    links.splice(to, 0, moved);
    const payload = links.map((m, i) => ({
      mediaId: m.mediaId,
      role: i === 0 ? ('hero' as const) : m.role === 'hero' ? ('gallery' as const) : m.role,
      sortOrder: i,
    }));
    try {
      await updateMut.mutateAsync({ mediaLinks: payload });
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec de la réorganisation des images'));
    }
  };

  const openCategoryModal = () => {
    setCatValue(product.categoryId);
    setCatModal(true);
  };

  const saveCategory = async () => {
    try {
      await updateMut.mutateAsync({ categoryId: catValue });
      setCatModal(false);
      toast.success('Catégorie mise à jour');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec du changement de catégorie'));
    }
  };

  return (
    <>
      <div className="crumbs" style={{ marginBottom: 14, fontSize: 12 }}>
        <a className="root" style={{ cursor: 'pointer' }} onClick={() => navigate('/catalogue')}>
          Catalogue
        </a>
        <span className="sep">
          <AdminIcon n="chevR" s={10} />
        </span>
        <button
          type="button"
          className="root"
          onClick={openCategoryModal}
          title="Changer la catégorie"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {parentCategoryName}
          <AdminIcon n="edit" s={10} />
        </button>
        <span className="sep">
          <AdminIcon n="chevR" s={10} />
        </span>
        <span className="here">{product.sku}</span>
      </div>

      <PageHead
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              onClick={() => navigate('/catalogue')}
            >
              <AdminIcon n="chevL" s={14} />
            </button>
            <span>{product.title}</span>
            <Tag variant={status.cls}>{status.l}</Tag>
          </span>
        }
        sub={
          <>
            <span className="mono">{product.sku}</span>
            {product.brand && <> · {product.brand}</>}
            <span className="muted">
              {product.publishedAt ? <> · Publié le {formatDate(product.publishedAt)}</> : null}
              {' · MAJ '}
              {formatDate(product.updatedAt)}
            </span>
          </>
        }
        actions={
          <>
            <AdminButton variant="ghost" onClick={openOnSite} title={publicUrl}>
              <AdminIcon n="eye" s={14} />
              Voir sur le site
            </AdminButton>
            <AdminButton variant="ghost" onClick={() => void printLabel()}>
              <AdminIcon n="print" s={14} />
              Étiquette
            </AdminButton>
            {product.status === 'draft' || product.status === 'paused' ? (
              <AdminButton variant="primary" onClick={publish} disabled={publishMut.isPending}>
                <AdminIcon n="check" s={14} />
                Publier
              </AdminButton>
            ) : product.status === 'published' ? (
              <AdminButton
                variant="ghost"
                onClick={() => unpublishMut.mutate(product.id)}
                disabled={unpublishMut.isPending}
              >
                Mettre en pause
              </AdminButton>
            ) : null}
            <AdminButton
              variant="ghost"
              disabled={inMarketingCart}
              onClick={() => {
                addToMarketingCart({
                  id: product.id,
                  title: product.title,
                  image: product.mediaLinks?.[0]?.url ?? null,
                  price: product.basePrice,
                  currency: product.currency,
                });
                toast.success('Ajouté au panier marketing.');
              }}
            >
              <AdminIcon n="trend" s={14} />
              {inMarketingCart ? 'Dans le panier marketing' : 'Panier marketing'}
            </AdminButton>
            <AdminButton
              variant="primary"
              onClick={() => navigate(`/catalogue/${product.id}/modifier`)}
            >
              <AdminIcon n="edit" s={14} />
              Modifier
            </AdminButton>
          </>
        }
      />

      <div className="detail">
        <div className="stack">
          {/* Médias */}
          <div className="card">
            <div className="card-h">
              <div>
                <h3>Médias · {product.mediaLinks.length} fichier(s)</h3>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <AdminButton
                  variant="ghost"
                  size="sm"
                  onClick={enhanceImages}
                  disabled={
                    !!product.aiEnhancedAt ||
                    enhanceImagesMut.isPending ||
                    product.mediaLinks.length === 0
                  }
                  title={
                    product.aiEnhancedAt
                      ? `Déjà amélioré le ${formatDate(product.aiEnhancedAt)}`
                      : undefined
                  }
                >
                  <AdminIcon n={enhanceImagesMut.isPending ? 'refresh' : 'star'} s={12} />
                  {product.aiEnhancedAt
                    ? 'Déjà amélioré ✓'
                    : enhanceImagesMut.isPending
                      ? 'Amélioration…'
                      : "Améliorer avec l'IA"}
                </AdminButton>
                <AdminButton
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/catalogue/${product.id}/modifier`)}
                >
                  <AdminIcon n="plus" s={12} />
                  Ajouter
                </AdminButton>
              </div>
            </div>
            <div className="card-b">
              {product.mediaLinks.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  Aucun média — ajoutez-en depuis l'écran d'édition.
                </p>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: 8,
                  }}
                >
                  {product.mediaLinks.slice(0, 10).map((m, i) => (
                    <div
                      key={m.id}
                      draggable
                      onDragStart={() => setDragIdx(i)}
                      onDragEnd={() => setDragIdx(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragIdx != null) void moveMedia(dragIdx, i);
                        setDragIdx(null);
                      }}
                      onClick={() => setPreview(i)}
                      title="Cliquer pour agrandir · Glisser pour réordonner"
                      style={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '1 / 1',
                        borderRadius: 'var(--a-r)',
                        overflow: 'hidden',
                        background: 'var(--a-surface-2)',
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'grab',
                        opacity: dragIdx === i || updateMut.isPending ? 0.45 : 1,
                        outline:
                          dragIdx != null && dragIdx !== i
                            ? '2px dashed var(--a-terracotta)'
                            : 'none',
                        outlineOffset: -2,
                        transition: 'opacity .15s',
                      }}
                    >
                      {m.url && (
                        <img
                          src={m.url}
                          alt={m.altText ?? ''}
                          draggable={false}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      )}
                      <div
                        style={{
                          position: 'absolute',
                          top: 6,
                          left: 6,
                          padding: '2px 6px',
                          background: 'rgba(255,255,255,0.9)',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--a-ink-2)',
                        }}
                      >
                        {i === 0 ? 'Principale' : `Photo ${i + 1}`}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void removeImage(m.mediaId, i === 0 ? 'Principale' : `Photo ${i + 1}`);
                        }}
                        disabled={deleteMediaMut.isPending}
                        title="Supprimer cette image"
                        style={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          width: 20,
                          height: 20,
                          display: 'grid',
                          placeItems: 'center',
                          padding: 0,
                          border: 'none',
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.9)',
                          color: 'var(--a-danger)',
                          cursor: 'pointer',
                        }}
                      >
                        <AdminIcon n="close" s={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div
                className="muted"
                style={{
                  fontSize: 11,
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <AdminIcon n="info" s={12} />
                La première photo est la principale. Glisser pour réordonner. Formats acceptés :
                JPG, PNG, WebP — 10 Mo max.
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="card">
            <div className="card-h">
              <div>
                <h3>Description</h3>
              </div>
            </div>
            <div className="card-b">
              {product.shortDescription && (
                <>
                  <div className="eyebrow" style={{ marginBottom: 4 }}>
                    Description courte
                  </div>
                  <p
                    style={{
                      margin: '0 0 16px',
                      fontSize: 13,
                      color: 'var(--a-ink-2)',
                    }}
                  >
                    {product.shortDescription}
                  </p>
                </>
              )}
              <div className="eyebrow" style={{ marginBottom: 4 }}>
                Description longue
              </div>
              {product.longDescription ? (
                <div className="md-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {product.longDescription}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  Pas de description longue.
                </p>
              )}
            </div>
            <style>{`
              .md-body { font-size: 13px; line-height: 1.6; color: var(--a-ink-2); }
              .md-body p { margin: 0 0 10px; }
              .md-body p:last-child { margin-bottom: 0; }
              .md-body h1, .md-body h2, .md-body h3, .md-body h4 {
                font-family: var(--a-font-display); margin: 16px 0 8px; line-height: 1.3;
              }
              .md-body h1 { font-size: 18px; }
              .md-body h2 { font-size: 16px; }
              .md-body h3 { font-size: 14px; }
              .md-body strong { font-weight: 700; color: var(--a-ink); }
              .md-body ul, .md-body ol { margin: 0 0 10px; padding-left: 22px; }
              .md-body li { margin: 4px 0; }
              .md-body a { color: var(--a-terracotta); text-decoration: underline; text-underline-offset: 2px; }
              .md-body code { background: var(--a-surface-2); padding: 1px 5px; border-radius: 3px; font-family: var(--a-font-mono); font-size: 12px; }
              .md-body pre { background: var(--a-surface-2); padding: 10px 12px; border-radius: 6px; overflow-x: auto; margin: 0 0 10px; }
              .md-body pre code { background: transparent; padding: 0; font-size: 12px; }
              .md-body blockquote { margin: 0 0 10px; padding: 4px 14px; border-left: 3px solid var(--a-terracotta); color: var(--a-ink-soft); background: var(--a-surface-2); border-radius: 0 4px 4px 0; }
              .md-body table { border-collapse: collapse; width: 100%; margin: 0 0 10px; font-size: 12px; }
              .md-body th, .md-body td { border: 1px solid var(--a-border); padding: 6px 10px; text-align: left; }
              .md-body th { background: var(--a-surface-2); font-weight: 600; }
              .md-body hr { border: 0; border-top: 1px solid var(--a-border); margin: 14px 0; }
            `}</style>
          </div>

          {/* P18 — Composition du pack (lecture seule) */}
          {product.kind === 'pack' && <PackCompositionCard productId={product.id} />}
          {/* P19 — visible seulement si le produit est éligible au njangi. */}
          <NjangiTiersCard productId={product.id} />

          {/* Déclinaisons — sur deux produits du catalogue sur trois il n'y en a
              qu'une, héritée de l'import : un tableau de huit colonnes pour une
              seule ligne faisait passer pour un choix ce qui n'en est pas un.
              La carte se réduit alors à ce qu'elle a à dire. */}
          {produitSimple ? (
            <div className="card">
              <div className="card-h">
                <div>
                  <h3>Déclinaisons</h3>
                  <p className="sub muted">
                    {declinaisons.length === 0
                      ? "Aucune — le produit ne peut pas être vendu en l'état"
                      : 'Produit simple — une seule référence en vente'}
                  </p>
                </div>
                <AdminButton
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/catalogue/${product.id}/modifier`)}
                >
                  <AdminIcon n="plus" s={12} />
                  {declinaisons.length === 0 ? 'Créer une déclinaison' : 'Ajouter une couleur ou une taille'}
                </AdminButton>
              </div>
              {reference && (
                <div className="card-b decli-simple">
                  <code className="mono">{reference.sku}</code>
                  <span className="muted">
                    {pluriel(variantStockTotal(reference), 'unité')} ·{' '}
                    {fmtFCFA(activeOfferPrice(product, reference))}
                  </span>
                  <Tag variant={reference.isActive ? 'success' : 'neutral'}>
                    {reference.isActive ? 'Active' : 'Inactive'}
                  </Tag>
                  {declinaisons.length > 1 && archivees > 0 && (
                    <span className="muted">
                      {archivees > 1
                        ? `+ ${archivees} anciennes déclinaisons désactivées, gardées`
                        : '+ 1 ancienne déclinaison désactivée, gardée'}{' '}
                      pour l'historique des commandes
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
          <div className="card">
            <div className="card-h">
              <div>
                <h3>Déclinaisons · {pluriel(declinaisons.length, 'variante')}</h3>
                <p className="sub muted">
                  {selectionnees.length > 0
                    ? `${pluriel(selectionnees.length, 'déclinaison')} ${accord(selectionnees.length, 'sélectionnée')}`
                    : 'Le stock et le prix se règlent par déclinaison'}
                </p>
              </div>

              <div className="decli-actions">
                {selectionnees.length > 0 && peutSupprimerDecli && (
                  <AdminButton
                    variant="primary"
                    size="sm"
                    className="btn-danger"
                    disabled={suppressionLot}
                    onClick={() =>
                      void supprimerLot(
                        selectionnees,
                        `Supprimer ${pluriel(selectionnees.length, 'déclinaison')}`,
                        <>
                          {selectionnees.map((v) => v.name).join(', ')}. Leur stock sera
                          ramené à zéro et leurs offres mises en pause.
                        </>,
                      )
                    }
                  >
                    <AdminIcon n="trash" s={12} />
                    {suppressionLot ? 'Suppression…' : 'Supprimer la sélection'}
                  </AdminButton>
                )}

                {selectionnees.length === 0 && declinaisons.length > 1 && peutSupprimerDecli && (
                  <AdminButton
                    variant="ghost"
                    size="sm"
                    disabled={suppressionLot}
                    title="Une seule déclinaison, avec le stock de toutes"
                    onClick={() => setConversionSimple(true)}
                  >
                    Passer en produit simple
                  </AdminButton>
                )}

                <AdminButton
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/catalogue/${product.id}/modifier`)}
                >
                  <AdminIcon n="plus" s={12} />
                  Variante
                </AdminButton>
              </div>
            </div>
            <div className="table-wrap" style={{ margin: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    {peutSupprimerDecli && declinaisons.length > 1 && (
                      <th style={{ width: 34 }}>
                        <input
                          type="checkbox"
                          aria-label="Tout sélectionner"
                          checked={selection.size === declinaisons.length}
                          ref={(el) => {
                            if (el)
                              el.indeterminate =
                                selection.size > 0 && selection.size < declinaisons.length;
                          }}
                          onChange={(e) =>
                            setSelection(
                              e.target.checked
                                ? new Set(declinaisons.map((v) => v.id))
                                : new Set(),
                            )
                          }
                        />
                      </th>
                    )}
                    <th style={{ width: 52 }}>
                      <span className="sr-only">Visuel</span>
                    </th>
                    <th>SKU</th>
                    <th>Nom</th>
                    <th>Options</th>
                    <th className="right">Stock</th>
                    <th className="right">Prix</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {product.variants.length === 0 ? (
                    <tr>
                      <td
                        colSpan={peutSupprimerDecli && declinaisons.length > 1 ? 9 : 8}
                        className="muted"
                        style={{ textAlign: 'center', padding: 16 }}
                      >
                        Aucune variante — créez-en au moins une depuis l'édition.
                      </td>
                    </tr>
                  ) : (
                    product.variants.map((v) => {
                      const stock = variantStockTotal(v);
                      const price = activeOfferPrice(product, v);
                      const tag =
                        v.isActive && stock > 0
                          ? { l: 'Active', cls: 'success' as TagClass }
                          : v.isActive
                            ? { l: 'Épuisée', cls: 'warning' as TagClass }
                            : { l: 'Inactive', cls: 'neutral' as TagClass };
                      const { hex, imageUrl, axeCouleur } = apparenceVariante(product, v);
                      const autresOptions = Object.entries(v.optionValues ?? {}).filter(
                        ([k]) => k !== axeCouleur,
                      );
                      const couleurLabel = axeCouleur
                        ? (v.optionValues ?? {})[axeCouleur]
                        : undefined;
                      return (
                        <tr key={v.id} className={selection.has(v.id) ? 'ligne-selectionnee' : undefined}>
                          {peutSupprimerDecli && declinaisons.length > 1 && (
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Sélectionner ${v.name}`}
                                checked={selection.has(v.id)}
                                onChange={(e) =>
                                  setSelection((prec) => {
                                    const suite = new Set(prec);
                                    if (e.target.checked) suite.add(v.id);
                                    else suite.delete(v.id);
                                    return suite;
                                  })
                                }
                              />
                            </td>
                          )}
                          <td>
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={v.name}
                                loading="lazy"
                                className="variant-thumb"
                              />
                            ) : (
                              <div
                                className="variant-thumb variant-thumb-vide"
                                title="Aucun visuel associé à cette déclinaison"
                              >
                                <AdminIcon n="image" s={13} />
                              </div>
                            )}
                          </td>
                          <td className="mono">{v.sku}</td>
                          <td>
                            <strong>{v.name}</strong>
                          </td>
                          <td>
                            {couleurLabel ? (
                              <span className="variant-couleur">
                                <span
                                  className="variant-pastille"
                                  style={{ background: hex ?? 'transparent' }}
                                  aria-hidden="true"
                                />
                                {/* La variante porte presque toujours le nom de
                                    sa couleur : le répéter ici remplirait une
                                    colonne entière pour rien. On ne le montre
                                    que lorsqu'il apporte une information. */}
                                {couleurLabel !== v.name && <span>{couleurLabel}</span>}
                                {hex ? (
                                  <code className="variant-hex">{hex.toUpperCase()}</code>
                                ) : (
                                  <span className="variant-hex-absent">teinte non définie</span>
                                )}
                              </span>
                            ) : null}
                            {autresOptions.length > 0 && (
                              <span className={couleurLabel ? 'variant-options-sup' : undefined}>
                                {autresOptions.map(([k, val]) => `${k} : ${val}`).join(' · ')}
                              </span>
                            )}
                            {!couleurLabel && autresOptions.length === 0 && (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td className="right tn">
                            {stock === 0 ? (
                              <span style={{ color: 'var(--a-danger)' }}>0</span>
                            ) : stock < 5 ? (
                              <span style={{ color: 'var(--a-warning)' }}>{stock}</span>
                            ) : (
                              stock
                            )}
                          </td>
                          <td className="right tn">{fmtFCFA(price)}</td>
                          <td>
                            <Tag variant={tag.cls}>{tag.l}</Tag>
                          </td>
                          <td>
                            <MenuVariante
                              produitId={product.id}
                              variante={v}
                              hex={hex}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* SEO */}
          <div className="card">
            <div className="card-h">
              <div>
                <h3>SEO & visibilité</h3>
              </div>
              <Tag variant="info" noDot>
                Score à venir
              </Tag>
            </div>
            <div className="card-b">
              <div className="info-row">
                <span className="l">URL publique</span>
                <a
                  className="v mono"
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--a-terracotta)',
                    textDecoration: 'none',
                  }}
                >
                  {publicUrl.replace(/^https?:\/\//, '')}
                </a>
              </div>
              <div className="info-row">
                <span className="l">Titre meta</span>
                <span className="v">{product.metaTitle ?? '—'}</span>
              </div>
              <div className="info-row">
                <span className="l">Description meta</span>
                <span className="v" style={{ maxWidth: 380, textAlign: 'right' }}>
                  {product.metaDescription ?? '—'}
                </span>
              </div>
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <span className="l">Mots-clés / tags</span>
                <span
                  className="v"
                  style={{
                    display: 'flex',
                    gap: 4,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  {product.tags.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    product.tags.map((t) => (
                      <Tag key={t} variant="neutral" noDot>
                        #{t}
                      </Tag>
                    ))
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="detail-side">
          {/* Tarification */}
          <div className="card">
            <div className="card-h">
              <div>
                <h3>Tarification</h3>
              </div>
            </div>
            <div className="card-b">
              <div className="info-row">
                <span className="l">Prix de vente public</span>
                <span className="v tn">
                  <strong>{fmtFCFA(product.basePrice)}</strong>
                </span>
              </div>
              {product.compareAtPrice && (
                <div className="info-row">
                  <span className="l">Prix barré (avant promo)</span>
                  <span className="v tn muted" style={{ textDecoration: 'line-through' }}>
                    {fmtFCFA(product.compareAtPrice)}
                  </span>
                </div>
              )}
              {costMin != null && (
                <>
                  <div className="info-row">
                    <span className="l">Coût d'achat (min variante)</span>
                    <span className="v tn">{fmtFCFA(costMin)}</span>
                  </div>
                  {marge != null && margePct != null && (
                    <div
                      className="info-row"
                      style={{
                        borderTop: '1px solid var(--a-border-strong)',
                        paddingTop: 10,
                        marginTop: 6,
                      }}
                    >
                      <span className="l" style={{ fontWeight: 700, color: 'var(--a-ink)' }}>
                        Marge brute
                      </span>
                      <span className="v tn">
                        <strong style={{ color: 'var(--a-success)' }}>{fmtFCFA(marge)}</strong>
                        {' · '}
                        {margePct.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </>
              )}
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <span className="l">Devise</span>
                <span className="v mono">{product.currency}</span>
              </div>
            </div>
          </div>

          {/* Inventaire */}
          <div className="card">
            <div className="card-h">
              <div>
                <h3>Inventaire</h3>
              </div>
              <AdminButton
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/catalogue/${product.id}/modifier`)}
              >
                Mouvement
              </AdminButton>
            </div>
            <div className="card-b">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <div
                  className="tn"
                  style={{
                    fontFamily: 'var(--a-font-display)',
                    fontWeight: 900,
                    fontSize: 32,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {stockSum}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  unité(s) au {hubLabel}
                </div>
              </div>
              <div className="info-row">
                <span className="l">Réservées (cmds en cours)</span>
                <span className="v tn">{reservedSum}</span>
              </div>
              <div className="info-row">
                <span className="l">Disponibles à la vente</span>
                <span className="v tn">
                  <strong>{availableSum}</strong>
                </span>
              </div>
              <div className="info-row">
                <span className="l">Seuil d'alerte stock bas</span>
                <span className="v tn">{stockMin}</span>
              </div>
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <span className="l">Hubs concernés</span>
                <span className="v">
                  {hubs.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    hubs.map((h) => (
                      <Tag key={h.id} variant="neutral" noDot>
                        {h.code}
                      </Tag>
                    ))
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Performance */}
          <div className="card">
            <div className="card-h">
              <div>
                <h3>Performance · 30 jours</h3>
              </div>
            </div>
            <div className="card-b">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 10,
                }}
              >
                <div>
                  <div className="eyebrow">Vues</div>
                  <div
                    className="tn"
                    style={{
                      fontFamily: 'var(--a-font-display)',
                      fontWeight: 900,
                      fontSize: 20,
                    }}
                  >
                    —
                  </div>
                </div>
                <div>
                  <div className="eyebrow">Ajouts panier</div>
                  <div
                    className="tn"
                    style={{
                      fontFamily: 'var(--a-font-display)',
                      fontWeight: 900,
                      fontSize: 20,
                    }}
                  >
                    —
                  </div>
                </div>
                <div>
                  <div className="eyebrow">Ventes</div>
                  <div
                    className="tn"
                    style={{
                      fontFamily: 'var(--a-font-display)',
                      fontWeight: 900,
                      fontSize: 20,
                      color: 'var(--a-success)',
                    }}
                  >
                    —
                  </div>
                </div>
                <div>
                  <div className="eyebrow">Conversion</div>
                  <div
                    className="tn"
                    style={{
                      fontFamily: 'var(--a-font-display)',
                      fontWeight: 900,
                      fontSize: 20,
                    }}
                  >
                    —
                  </div>
                </div>
              </div>
              <div className="divider" />
              <div className="eyebrow" style={{ marginBottom: 8 }}>
                Stock par variante
              </div>
              {topVariants.length === 0 ? (
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Pas encore de variante.
                </p>
              ) : (
                topVariants.map((v, i) => (
                  <div key={i} style={{ padding: '5px 0' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 12,
                        marginBottom: 3,
                      }}
                    >
                      <span>{v.k}</span>
                      <strong className="tn">{v.n}</strong>
                    </div>
                    <div className="bar thin">
                      <i style={{ width: (v.n / topMax) * 100 + '%' }} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Avis clients (placeholder phase 1) */}
          <div className="card">
            <div className="card-h">
              <div>
                <h3>Avis clients</h3>
              </div>
              <Tag variant="info">0</Tag>
            </div>
            <div className="card-b" style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: 'var(--a-font-display)',
                  fontWeight: 900,
                  fontSize: 36,
                  color: 'var(--a-ochre)',
                  lineHeight: 1,
                }}
              >
                —
              </div>
              <div
                style={{
                  color: 'var(--a-ochre)',
                  fontSize: 16,
                  margin: '4px 0',
                }}
              >
                ☆☆☆☆☆
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Aucun avis pour l'instant
              </div>
            </div>
          </div>

          {product.status !== 'archived' && (
            <AdminButton
              variant="ghost"
              onClick={archiveProduct}
              style={{ width: '100%', color: 'var(--a-danger)' }}
            >
              <AdminIcon n="alert" s={14} />
              Archiver
            </AdminButton>
          )}
        </div>
      </div>

      {/* Lightbox : image en grand + navigation */}
      {preview != null && product.mediaLinks[preview] && (
        <Modal
          title={
            preview === 0
              ? 'Photo principale'
              : `Photo ${preview + 1} / ${product.mediaLinks.length}`
          }
          subtitle={product.mediaLinks[preview].altText ?? product.title}
          onClose={() => setPreview(null)}
          width={760}
        >
          <div
            style={{
              position: 'relative',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <img
              src={product.mediaLinks[preview].url ?? ''}
              alt={product.mediaLinks[preview].altText ?? product.title}
              style={{
                maxWidth: '100%',
                maxHeight: '65vh',
                objectFit: 'contain',
                borderRadius: 8,
                background: 'var(--a-surface-2)',
              }}
            />
            {product.mediaLinks.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Image précédente"
                  onClick={() =>
                    setPreview(
                      (preview + product.mediaLinks.length - 1) % product.mediaLinks.length,
                    )
                  }
                  style={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '1px solid var(--a-border)',
                    background: 'rgba(255,255,255,0.92)',
                    cursor: 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <AdminIcon n="chevL" s={16} />
                </button>
                <button
                  type="button"
                  aria-label="Image suivante"
                  onClick={() => setPreview((preview + 1) % product.mediaLinks.length)}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '1px solid var(--a-border)',
                    background: 'rgba(255,255,255,0.92)',
                    cursor: 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <AdminIcon n="chevR" s={16} />
                </button>
              </>
            )}
          </div>
        </Modal>
      )}

      {conversionSimple && (
        <ConvertToSimpleModal
          productId={product.id}
          variants={declinaisons}
          defaultVariantId={product.defaultVariantId}
          onClose={() => setConversionSimple(false)}
        />
      )}

      {/* Changement rapide de catégorie */}
      {catModal && (
        <Modal
          title="Changer la catégorie"
          subtitle="Le produit sera déplacé immédiatement dans le nouveau rayon."
          onClose={() => setCatModal(false)}
          width={520}
        >
          <CategoryPicker
            categories={cats ?? []}
            value={catValue}
            onChange={setCatValue}
            testId="quick-category-picker"
            inline
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 16,
            }}
          >
            <AdminButton variant="ghost" onClick={() => setCatModal(false)}>
              Annuler
            </AdminButton>
            <AdminButton
              variant="primary"
              disabled={!catValue || catValue === product.categoryId || updateMut.isPending}
              onClick={() => void saveCategory()}
            >
              {updateMut.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </AdminButton>
          </div>
        </Modal>
      )}
    </>
  );
}
